import { useMemo } from "react";
import { MODULE_KEYS_MAP, routeKeys, type ModuleKey } from "@/api/permissions/permissions.types";
import ServEngineIcon from "@/assets/servengine-icon.svg";
import ServEngineLogo from "@/assets/servengine.svg";
import {
  BARE_BONES_HIDDEN_ROUTES,
  ENTITY_HIDDEN_ROUTES,
  isEntityLevelRoute,
  isMergedViewHiddenRoute,
  MAIN_APP_HIDDEN_ROUTES,
} from "@/constants/routesConfig";
import { actingTenantFor, effectiveAccessQueryOptions } from "@/api/access/access.queries";
import { resolveSwitchFromPath } from "@/api/permissions/permissions.api";
import { useEntityTenantStore } from "@/store/entityTenantStore";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { holdsInScope } from "@/utils/access";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useInsideEntity, useLeaveWhenMergedView, useMergedView } from "@/hooks/usePlatformView";
import { getRouteIcon, routeIconKeys } from "@/utils/getRouteIconComponent";
import { isAppLevelUser } from "@/utils/isPlatformView";
import { isReactiveHireRep } from "@/utils/reactiveHire/isReactiveHireRep";
import { ActionIcon, Box, Divider, Group, Image, ScrollArea, Stack, Text } from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconCreditCard,
  IconFolders,
  // IconLayoutGrid,  // SERV-2626: unused while the Overview entry above is commented out
  IconMail,
  IconLink,
  IconMap2,
  IconPlugConnected,
  IconShoppingCart,
  IconTag,
  IconTruckReturn,
  IconBuildingWarehouse,
  IconCalendarEvent,
  IconWallet,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import LabelMenu from "../LabelMenu/LabelMenu";
import SidebarIndicatorBadge from "../SidebarIndicatorBadge/SidebarIndicatorBadge";
import SidebarItem from "./SidebarItem";

type SidebarProps = {
  opened: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  onClose?: () => void;
};

type Item = {
  to: string;
  label: string;
  icon: React.ReactNode;
  badgeCount?: number;
  module: ModuleKey;
  exact?: boolean;
};

export type Section = {
  label: string;
  items: Item[];
};

export const sidebarSections: Section[] = [
  {
    label: "",
    items: [
      { to: "/", label: "Home", icon: getRouteIcon(routeKeys.home), module: MODULE_KEYS_MAP.home },
      {
        to: "/my-tasks",
        label: "My Tasks",
        icon: getRouteIcon(routeKeys.myTasks),
        // badgeCount: 8, // TODO: Update badge count when the feature is implemented
        module: MODULE_KEYS_MAP.myTasks,
      },
      {
        to: "/sp-calendar",
        label: "SP Calendar",
        icon: getRouteIcon(routeKeys.spCalendar),
        module: MODULE_KEYS_MAP.spCalendar,
      },
      {
        to: "/reports",
        label: "Reports",
        icon: getRouteIcon(routeKeys.reports),
        module: MODULE_KEYS_MAP.reports,
      },
      {
        to: "/chat",
        label: "Chat",
        icon: getRouteIcon(routeKeys.chat),
        module: MODULE_KEYS_MAP.chat,
      },
      {
        to: "/email-ai",
        label: "Email AI",
        icon: <IconMail size={18} />,
        // SERV-3232: gated on the email_ai module the roles matrix grants.
        module: MODULE_KEYS_MAP.emailAi,
      },
      {
        to: "/files",
        label: "Files",
        icon: <IconFolders size={18} />,
        module: MODULE_KEYS_MAP.files,
      },
    ],
  },
  {
    label: "Management",
    items: [
      {
        to: "/entities",
        label: "Entities",
        icon: getRouteIcon(routeKeys.entities),
        module: MODULE_KEYS_MAP.entities,
      },
      {
        to: "/clients",
        label: "Clients",
        icon: getRouteIcon(routeKeys.clients),
        module: MODULE_KEYS_MAP.clients,
      },
      {
        to: "/locations",
        label: "Locations",
        icon: getRouteIcon(routeKeys.locations),
        module: MODULE_KEYS_MAP.locations,
      },
      {
        to: "/service-providers",
        label: "Service Providers",
        icon: getRouteIcon(routeKeys.serviceProviders),
        module: MODULE_KEYS_MAP.serviceProviders,
      },
      {
        to: "/work-orders",
        label: "Work Orders",
        icon: getRouteIcon(routeKeys.workOrders),
        module: MODULE_KEYS_MAP.workOrders,
      },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        to: "/assets",
        label: "Assets",
        icon: getRouteIcon(routeKeys.assets),
        module: MODULE_KEYS_MAP.assets,
      },
      {
        to: "/proposals",
        label: "Proposals",
        icon: getRouteIcon(MODULE_KEYS_MAP.proposals),
        module: MODULE_KEYS_MAP.proposals,
      },
      {
        to: "/form-builder/templates",
        label: "Form Builder",
        icon: getRouteIcon(MODULE_KEYS_MAP.formBuilder),
        module: MODULE_KEYS_MAP.formBuilder,
      },
      {
        to: "/dispatch-queue",
        label: "Dispatch Queue",
        icon: getRouteIcon(MODULE_KEYS_MAP.dispatchQueue),
        module: MODULE_KEYS_MAP.dispatchQueue,
      },
      {
        // SERV-3137: v1 reuses the Dispatch Queue permission - see requiredModuleByPath.
        // A distinct icon (not getRouteIcon(dispatchQueue)'s own) keeps the two items from
        // reading as duplicates in the nav.
        to: "/live-map",
        label: "Live Map",
        icon: <IconMap2 size={18} />,
        module: MODULE_KEYS_MAP.dispatchQueue,
      },
      {
        to: "/check-in-out",
        label: "Check-in/out",
        icon: getRouteIcon(routeKeys.checkInOut),
        // Reuses the work order permission until the External Check-in/Check-out Admin role exists.
        module: MODULE_KEYS_MAP.workOrders,
      },
    ],
  },
  {
    label: "Procurement",
    items: [
      /*
       * SERV-2626: taken out of the sidebar. The label shipped a person's name as production nav
       * text on a section every permitted user sees. Commented out rather than deleted, per the
       * MVP hide-don't-delete rule - the route and the page still exist and are reachable at
       * /procurement/overview, so restoring this means uncommenting and giving it a real name.
       */
      // {
      //   to: "/procurement/overview",
      //   label: "Overview",
      //   icon: <IconLayoutGrid size={18} />,
      //   module: MODULE_KEYS_MAP.procurement,
      // },
      {
        to: "/procurement",
        label: "Procurement Queue",
        icon: <IconShoppingCart size={18} />,
        module: MODULE_KEYS_MAP.procurement,
        exact: true,
      },
      {
        to: "/procurement/pricing",
        label: "Pricing Requests",
        icon: <IconTag size={18} />,
        module: MODULE_KEYS_MAP.procurement,
      },
      {
        to: "/procurement/returns",
        label: "Returns",
        icon: <IconTruckReturn size={18} />,
        module: MODULE_KEYS_MAP.procurement,
      },
      {
        to: "/procurement/accounts",
        label: "Supplier Accounts",
        icon: <IconWallet size={18} />,
        module: MODULE_KEYS_MAP.procurement,
      },
      {
        // SERV-2663: a unit outlives the request that created it, so it needs a way in of its own.
        to: "/procurement/storage-units",
        label: "Storage Units",
        icon: <IconBuildingWarehouse size={18} />,
        module: MODULE_KEYS_MAP.procurement,
      },
      {
        to: "/procurement/calendar",
        label: "Calendar",
        icon: <IconCalendarEvent size={18} />,
        module: MODULE_KEYS_MAP.procurement,
      },
    ],
  },
  {
    label: "Financials",
    items: [
      {
        to: "/accounts-payable",
        label: "Accounts Payable",
        icon: getRouteIcon(routeKeys.accountsPayable),
        module: MODULE_KEYS_MAP.accountsPayable,
      },
      {
        to: "/accounts-payable/sp-payment-methods",
        label: "SP Payment Methods",
        icon: <IconCreditCard size={18} />,
        module: MODULE_KEYS_MAP.accountsPayable,
      },
      {
        to: "/close-out",
        label: "Close Out",
        icon: getRouteIcon(routeKeys.closeOut),
        module: MODULE_KEYS_MAP.closeOut,
      },
      {
        to: "/accounts-receivable",
        label: "Accounts Receivable",
        icon: getRouteIcon(routeKeys.accountsReceivable),
        module: MODULE_KEYS_MAP.accountsReceivable,
      },
    ],
  },
  {
    label: "Settings",
    items: [
      {
        to: "/roles-permissions",
        label: "Roles & Permissions",
        icon: getRouteIcon(routeKeys.rolesPermissions),
        module: MODULE_KEYS_MAP.rolesPermissions,
      },
      {
        to: "/user-management",
        label: "User Management",
        icon: getRouteIcon(routeKeys.userManagement),
        module: MODULE_KEYS_MAP.userManagement,
      },
      {
        to: "/form-manager",
        label: "Dropdown & Form Manager",
        icon: getRouteIcon(routeKeys.settings),
        module: MODULE_KEYS_MAP.settings,
      },
      {
        to: "/settings",
        label: "View all Settings",
        icon: getRouteIcon(routeKeys.settings),
        module: MODULE_KEYS_MAP.settings,
      },
    ],
  },
];

const REACTIVE_HIRE_QUEUE_ITEM: Item = {
  to: "/service-providers/reactive-hire-requests",
  label: "Reactive Hire Requests",
  icon: getRouteIcon(routeIconKeys.reactiveHireRequests),
  module: MODULE_KEYS_MAP.serviceProviders,
};

const W9_REQUESTS_ITEM: Item = {
  to: "/service-providers/w9-requests",
  label: "W-9 Requests",
  icon: getRouteIcon(routeIconKeys.w9Requests),
  module: MODULE_KEYS_MAP.w9Requests,
};

export default function Sidebar({
  opened: desktopOpened,
  onToggle,
  isMobile = false,
  onClose,
}: SidebarProps) {
  const opened = desktopOpened || isMobile;
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const { isMainAppLogin, isEntityLogin, userRoles, userType, isAppLevel, allEntities } =
    useActiveSession();
  const { filterSidebar, isSuperAdmin } = usePermissions();
  // The guard asks a menu link's switch at the entity scope (a link carries no ?tenant=), and not at
  // all while impersonating, so the menu asks the same way rather than through an open record's
  // company.
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);
  const { data: menuAccess } = useQuery({
    ...effectiveAccessQueryOptions(entityTenant),
    retry: false,
    enabled: !isImpersonating,
  });
  const menuActingTenant = actingTenantFor(entityTenant, { userType, isImpersonating });
  const insideEntity = useInsideEntity();
  const mergedView = useMergedView();
  useLeaveWhenMergedView();
  const appLevelUser = isAppLevelUser({ isAppLevel, allEntities: allEntities ?? false, userType });
  const allSections = useMemo<Section[]>(
    () =>
      isSuperAdmin && !isEntityLogin
        ? [
            ...sidebarSections,
            {
              label: "Oversight",
              items: [
                {
                  to: "/fm-integrations",
                  label: "FM Integrations",
                  icon: <IconPlugConnected size={18} />,
                  module: MODULE_KEYS_MAP.home,
                },
                // SERV-2806. Nothing linked to /short-links, so the screens
                // were reachable only by typing the URL, which puts an admin
                // back to calling the API by hand -- the exact thing the
                // ticket exists to avoid.
                //
                // This section is already gated on `isSuperAdmin &&
                // !isEntityLogin`, which is the same condition requireSuperAdmin
                // applies in the route's beforeLoad, so the nav entry and the
                // guard cannot drift apart. `module` is home for the same
                // reason FM Integrations uses it: there is no deeplink
                // permission code, and adding one needs a per-tenant
                // Permissions backfill.
                {
                  to: "/short-links",
                  label: "Short Links",
                  icon: <IconLink size={18} />,
                  module: MODULE_KEYS_MAP.home,
                },
              ],
            },
          ]
        : sidebarSections,
    [isSuperAdmin, isEntityLogin],
  );

  const sectionsWithReactiveHire = isReactiveHireRep(userRoles, userType)
    ? allSections.map((section) => ({
        ...section,
        items: section.items.flatMap((item) =>
          item.to === "/service-providers" ? [item, REACTIVE_HIRE_QUEUE_ITEM] : [item],
        ),
      }))
    : allSections;

  const sectionsWithW9Requests = sectionsWithReactiveHire.map((section) => ({
    ...section,
    items: section.items.flatMap((item) =>
      item.to === "/service-providers" ? [item, W9_REQUESTS_ITEM] : [item],
    ),
  }));

  // A page the guard opens on a switch (contract 7.4) is not offered to someone it would send home.
  const holdsRouteSwitch = (item: Item) => {
    const routeSwitch = resolveSwitchFromPath(item.to);
    if (!routeSwitch || isImpersonating || !menuAccess) return true;
    return holdsInScope(menuAccess, menuActingTenant, routeSwitch);
  };

  const filteredSidebarSections = filterSidebar(sectionsWithW9Requests)
    .map((section) => ({ ...section, items: section.items.filter(holdsRouteSwitch) }))
    .map((section) => ({
      ...section,
      items:
        isMainAppLogin && !(appLevelUser && insideEntity)
          ? section.items.filter((item) => !MAIN_APP_HIDDEN_ROUTES.has(item.to))
          : section.items,
    }))
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !BARE_BONES_HIDDEN_ROUTES.has(item.to)),
    }))
    .map((section) => ({
      ...section,
      items: insideEntity
        ? section.items.filter((item) => !ENTITY_HIDDEN_ROUTES.has(item.to))
        : section.items,
    }))
    .map((section) => ({
      ...section,
      items: mergedView
        ? section.items.filter((item) => !isMergedViewHiddenRoute(item.to))
        : section.items,
    }))
    .map((section) => ({
      ...section,
      items: mergedView
        ? section.items.filter((item) => !isEntityLevelRoute(item.to))
        : section.items,
    }))
    .filter((section) => section.items.length > 0);

  const activeItemTo = filteredSidebarSections
    .flatMap((section) => section.items)
    .filter(
      (item) =>
        currentPath === item.to ||
        (!item.exact && item.to !== "/" && currentPath.startsWith(`${item.to}/`)),
    )
    .reduce<string | null>(
      (best, item) => (best === null || item.to.length > best.length ? item.to : best),
      null,
    );

  const labelMenu = <LabelMenu opened={opened} />;

  return (
    <Stack gap={6} style={{ height: "100%", position: "relative" }}>
      <Stack
        px="xs"
        py={0}
        h={57}
        m={0}
        style={{ borderBottom: "1px solid var(--border-default)" }}
        justify="center"
      >
        {isMobile ? (
          <Group gap="xs" wrap="nowrap">
            <Box style={{ flex: 1, minWidth: 0 }}>{labelMenu}</Box>
            <ActionIcon
              variant="subtle"
              size={44}
              c="var(--icon-default)"
              onClick={onClose}
              aria-label="Close navigation"
            >
              <IconX size={20} />
            </ActionIcon>
          </Group>
        ) : (
          labelMenu
        )}
      </Stack>

      <ScrollArea style={{ flex: 1, scrollbarWidth: "none" }} type="never">
        <Stack gap={6} px="xs" py="xs" align={opened ? "start" : "center"}>
          {filteredSidebarSections.map((section, sectionIndex) => (
            <Stack key={section.label || sectionIndex} gap={6} w={opened ? "100%" : "auto"}>
              {sectionIndex > 0 && !opened && <Divider />}

              {section.label && opened ? (
                <Text size="xs" c="dimmed" px="xs" pt="xs">
                  {section.label}
                </Text>
              ) : null}

              {section.items.map((item) => {
                const isActive = item.to === activeItemTo;
                const right = item?.badgeCount ? (
                  <div style={{ marginTop: "-10px", marginRight: "10px" }}>
                    <SidebarIndicatorBadge badgeCount={item?.badgeCount} position="middle-center" />
                  </div>
                ) : undefined;

                return (
                  <SidebarItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    icon={item.icon}
                    opened={opened}
                    active={isActive}
                    exact={item.exact}
                    right={right}
                    badgeCount={item?.badgeCount}
                    onNavigate={onClose}
                  />
                );
              })}
            </Stack>
          ))}
          {/* adds a little bottom padding so the floating toggle never overlaps the last item */}
          <div style={{ height: 90 }} />
        </Stack>
      </ScrollArea>

      <Stack gap={6} px="xs" py="sm">
        {opened ? (
          <Group gap="xs" style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }} pt="md">
            <Image src={ServEngineIcon} w="auto" h={18} alt="ServEngine icon" />
            <Text size="xs" c="dimmed">
              Powered by
            </Text>
            <Image src={ServEngineLogo} w="auto" h={16} alt="ServEngine logo" />
          </Group>
        ) : (
          <Box
            style={{
              borderTop: "1px solid var(--mantine-color-gray-3)",
            }}
            py="md"
          >
            <Image src={ServEngineIcon} w={18} h={18} alt="ServEngine icon" mx="auto" my={0} />
          </Box>
        )}
      </Stack>

      {!isMobile && (
        <ActionIcon
          variant="white"
          radius="sm"
          onClick={onToggle}
          aria-label="Toggle navigation"
          style={{
            position: "absolute",
            right: -13,
            bottom: "10%",
            boxShadow: "var(--mantine-shadow-sm)",
            border: "2px solid var(--mantine-color-gray-3)",
          }}
        >
          {opened ? (
            <IconChevronLeft size={16} color="var(--mantine-color-gray-9)" />
          ) : (
            <IconChevronRight size={16} color="var(--mantine-color-gray-9)" />
          )}
        </ActionIcon>
      )}
    </Stack>
  );
}
